import * as React from "react";
import { Calendar, Divider, Drawer, Col, Row, Badge, Form } from "antd";
import * as moment from "moment";

import { CommandBar } from 'src/core/ui/command-bar';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { ZuluIcon } from 'src/app/shared/icon';

interface Props {
	mode: ControlMode;
	model: TourItineraryItem;
}

interface State {
	showDrawer: boolean;
	minDay?: Date;
	maxDay?: Date;
}

const format = "YYYYMMDD";

export class DepartureDates extends React.Component<Props, State> {
	public state: State = {
		showDrawer: false,
	};

	private options = [
		{
			label: "Every week",
			value: 0,
		},
		{
			label: "This quarter (weekly)",
			value: 1,
		},
		{
			label: "This year (weekly)",
			value: 2,
		},
		{
			label: "Custom",
			value: 3,
		},
	];

	constructor(props: Props) {
		super(props);

		const { dynamicProperties } = props.model;
		if (dynamicProperties['weekdays'] == null) {
			dynamicProperties['weekdays'] = [false, false, false, false, false, false, false];
		}
		if (dynamicProperties.dateType == null) {
			dynamicProperties.dateType = 0;
		}
		if (dynamicProperties.days == null) {
			dynamicProperties.days = [];
		}
	}

	public componentDidMount() {
		const { minDay, maxDay } = this.getRangeByDates();
		this.setState({
			minDay: minDay ? minDay.toDate() : undefined,
			maxDay: maxDay ? maxDay.toDate() : undefined,
		});
	}

	public render(): React.ReactNode {
		const { mode, model } = this.props;
		const { showDrawer, minDay, maxDay } = this.state;

		const weekdays = model.dynamicProperties['weekdays'];
		const dateType = model.dynamicProperties['dateType'];
		const days = model.dynamicProperties['days'];

		const months = minDay && maxDay ? moment.utc(maxDay).diff(minDay, 'month', true) : 0;
		let monthsCalendar: React.ReactNode[] = [];
		if (months > 0) {
			let index = 0;
			const start = moment.utc(minDay);
			while (months >= index) {
				monthsCalendar.push(
					<CustomCalendar
						key={`${start.year()}-${start.month()}`}
						start={start.clone()}
						dateType={dateType}
						days={days}
						weekdays={weekdays}
					/>
				);
				start.add(1, 'month');
				index++;
			}
		}

		let weekDaysView: React.ReactNode = null;
		if (weekdays && weekdays.length > 0 && weekdays.length < 8) {
			let items = weekdays.map((day, index) => {
				let item = {
					key: index.toString(),
					name: this.getDayWeek(index),
					onClick: (ev) => { this.onChange(index, ev) },
					type: undefined
				}
				if (day == true) {
					item.type = "primary";
				}
				return item;

			});
			weekDaysView = (
				<CommandBar
					key="weekdays"
					mode={mode}
					items={items}
				/>
			);
		}

		return (
			<React.Fragment>
				<Drawer
					visible={showDrawer}
					onClose={this.changeDrawerVisibility}
					title="Custom Dates"
					width="600px"
					style={{ overflow: 'auto' }}
				>
					<Row gutter={16}>
						<Col xs={12}>
							<DateView
								disabled={dateType !== 3}
								required={true}
								mode={mode}
								format="date"
								label="From"
								value={minDay}
								onChanged={minDay => this.setState({ minDay })}
							/>
						</Col>
						<Col xs={12}>
							<DateView
								disabled={dateType !== 3}
								required={true}
								mode={mode}
								format="date"
								label="From"
								value={maxDay}
								onChanged={maxDay => this.setState({ maxDay })}
							/>
						</Col>
					</Row>
					<Divider />
					<Row>
						{monthsCalendar.length > 0 && monthsCalendar}
					</Row>
				</Drawer>
				<Row gutter={16}>
					<Col xs={24}>
						<DropdownView
							required={true}
							disabled={mode === "display"}
							label="Tour Dates"
							options={this.options}
							model={dateType}
							onChanged={val => {
								model.dynamicProperties.dateType = val.value;
								if (val.value !== 0) {
									let minDay: moment.Moment;
									let maxDay: moment.Moment;
									switch (val.value) {
										case 1:
											minDay = moment.utc().startOf('quarter');
											maxDay = moment.utc().endOf('quarter');
											break;
										case 2:
											minDay = moment.utc().startOf('year');
											maxDay = moment.utc().endOf('year');
											break;
										case 3:
											const result = this.getRangeByDates();
											minDay = result.minDay;
											maxDay = result.maxDay;
											break;
									}
									if (minDay && maxDay) {
										const iterator = minDay.clone();
										const days: string[] = [];
										while (maxDay.isSameOrAfter(iterator)) {
											const day = iterator.day()
											if (weekdays[day === 0 ? 6 : day - 1]) {
												days.push(iterator.format(format));
											}
											iterator.add(1, "day");
										}
										model.dynamicProperties['days'] = days;
									}
									this.setState({
										showDrawer: true,
										minDay: minDay ? minDay.toDate() : undefined,
										maxDay: maxDay ? maxDay.toDate() : undefined,
									});
								}
							}}
							dropdownRender={dateType !== 0 ? this.showCustomDates : undefined}
						/>
					</Col>
					<Col xs={24}>
						{weekDaysView}
					</Col>
				</Row>
			</React.Fragment>
		);
	}

	private getDayWeek = (index: number) => {
		switch (index) {
			case 0:
				return "Monday"
			case 1:
				return "Thuesday"
			case 2:
				return "Wednesday"
			case 3:
				return "Thursday"
			case 4:
				return "Friday"
			case 5:
				return "Saturday"
			case 6:
				return "Sunday"
		}
	}

	private getRangeByDates = () => {
		const days = this.props.model.dynamicProperties['days'];
		const minDay = days.length > 0 ? moment.utc(Math.min(...days), format) : undefined;
		const maxDay = days.length > 0 ? moment.utc(Math.max(...days), format) : undefined;
		return { minDay, maxDay };
	}

	private onChange = (e, ev) => {
		const { dynamicProperties } = this.props.model;
		dynamicProperties['weekdays'][e] = !dynamicProperties['weekdays'][e];
	}

	private showCustomDates = menu => (
		<div>
			{menu}
			<Divider style={{ margin: '4px 0' }} />
			<div style={{ padding: '8px', cursor: 'pointer' }} onClick={this.changeDrawerVisibility}>
				<ZuluIcon type="calendar" /> Custom dates
			</div>
		</div>
	);

	private changeDrawerVisibility = () => {
		this.setState({
			showDrawer: !this.state.showDrawer,
		});
	}
}

interface Props1 {
	start: moment.Moment;
	dateType: number;
	days: string[];
	weekdays: [boolean, boolean, boolean, boolean, boolean, boolean, boolean];
}

class CustomCalendar extends React.Component<Props1, {}> {
	public render(): React.ReactNode {
		const { start, dateType } = this.props;

		return (
			<Col xs={24}>
				<h4 className="calendar-header">{dateType !== 3 ? `${start.year()}-` : null}{moment.months(start.month())}</h4>
				<Calendar
					fullscreen={false}
					prefixCls="disable-ant-fullcalendar"
					value={start}
					dateFullCellRender={this.dateRender}
				/>
			</Col>
		);
	}

	private dateRender = (item: moment.Moment): React.ReactNode => {
		const { start, days } = this.props;

		if (!start.isSame(item, "month")) {
			return null;
		}

		const itemFormatted = item.format(format);
		const isSelected = days.indexOf(itemFormatted) >= 0;

		return (
			<div id={item.toISOString(true)} className="cursor" onClick={this.selectionChange(itemFormatted)}>
				{isSelected && <Badge status="success" text={item.date().toString()} />}
				{!isSelected && <span>{item.date()}</span>}
			</div>
		);
	}

	private selectionChange = (item: string) => () => {
		const { days } = this.props;

		const isSelected = days.indexOf(item);
		if (isSelected >= 0) {
			days.splice(isSelected, 1);
		} else {
			days.push(item);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/form/parts/tour-itinerary-departure-dates.view.tsx